/**
 * VoiceTest — direct WebRTC call into the Vooice agent, with diagnostic
 * panes for live transcript and tool calls.
 *
 * Mounts at /app/calls/test. Flow:
 *   1. POST /api/voice/web-call — mints LiveKit token + dispatches "vooice"
 *      worker into a fresh room.
 *   2. Connect via livekit-client, publish mic, attach remote audio.
 *   3. Poll /api/voice/sessions/by-room/<room> until the agent registers
 *      a voice_sessions row (≤1s after dispatch).
 *   4. Once we have a session_id, poll /api/voice/sessions/:id/events/poll
 *      every 1s. Render user.transcript / agent.utterance as bubbles.
 *      Render tool.call entries in a sidebar.
 *
 * Audio diagnostics:
 *   - Local mic level meter (Web Audio API analyser on the mic stream).
 *   - Explicit play() on remote audio attach to surface autoplay blocks.
 *   - Visible "Unmute audio" button if the browser blocks playback.
 */
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Room,
  RoomEvent,
  Track,
  type RemoteTrack,
  type RemoteParticipant,
  type LocalAudioTrack,
} from "livekit-client";
import { voice, type VoiceTimingEvent } from "../api/client";
import { Button, Card, LiveDot, Mono, Pill, Wave } from "../components/v3";

type Status =
  | "idle"
  | "connecting"
  | "connected"
  | "agent_joined"
  | "ended"
  | "error";

const STATUS_LABEL: Record<Status, string> = {
  idle: "Ready",
  connecting: "Connecting…",
  connected: "Waiting for Vooice",
  agent_joined: "Live",
  ended: "Call ended",
  error: "Error",
};

const STATUS_TONE: Record<
  Status,
  "neutral" | "amber" | "ok" | "rose" | "warn"
> = {
  idle: "neutral",
  connecting: "warn",
  connected: "amber",
  agent_joined: "ok",
  ended: "neutral",
  error: "rose",
};

interface TranscriptTurn {
  id: number;
  ts_ms: number;
  speaker: "user" | "agent";
  text: string;
}

interface ToolCall {
  id: number;
  ts_ms: number;
  tool: string;
  status: string;
  duration_ms: number | null;
  details: Record<string, unknown>;
}

/** Unified pipeline event — every audit_log row tied to the session, in order.
 *  Surface for "everything that happened" so latency, gaps, and stage flow
 *  are visible alongside the conversation. */
interface ActivityEvent {
  id: number;
  ts_ms: number;
  action: string;
  tool: string | null;
  status: string;
  duration_ms: number | null;
  summary: string;
  category: ActivityCategory;
}

type ActivityCategory =
  | "user"
  | "agent"
  | "tool"
  | "stage"
  | "policy"
  | "anomaly"
  | "system";

interface LatencyStat {
  /** ts_ms of the user transcript that prompted the response. */
  user_ts: number;
  /** ts_ms of the agent's first response after that user turn. */
  agent_ts: number;
}

export function VoiceTest() {
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);
  const [room, setRoom] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [callStarted, setCallStarted] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [transcript, setTranscript] = useState<TranscriptTurn[]>([]);
  const [toolCalls, setToolCalls] = useState<ToolCall[]>([]);
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const [latencies, setLatencies] = useState<LatencyStat[]>([]);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [micLevel, setMicLevel] = useState(0); // 0..1
  const [remoteSpeaking, setRemoteSpeaking] = useState(false);

  const roomRef = useRef<Room | null>(null);
  const audioContainerRef = useRef<HTMLDivElement | null>(null);
  const remoteAudioElsRef = useRef<HTMLMediaElement[]>([]);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const transcriptScrollRef = useRef<HTMLDivElement | null>(null);

  // Tick elapsed seconds while live.
  useEffect(() => {
    if (!callStarted || status === "ended" || status === "error") return;
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - callStarted) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [callStarted, status]);

  // Resolve room → session_id, then poll events.
  useEffect(() => {
    if (!room || sessionId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await voice.getSessionByRoom(room);
        if (!cancelled) setSessionId(s.id);
      } catch {
        /* 404 is expected until the agent registers; keep polling */
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [room, sessionId]);

  // Once we have a session, poll its event stream and fan out into
  // transcript / tool / activity buckets. Activity is a superset — every
  // audit_log row, in order, with a short readable summary so the user
  // sees the full pipeline (stage transitions, policy checks, tripwires,
  // recap delivery) and not just the conversation slice.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let sinceTs = 0;

    const tick = async () => {
      if (cancelled) return;
      try {
        const res = await voice.pollSessionEvents(sessionId, sinceTs);
        if (cancelled) return;

        const newTurns: TranscriptTurn[] = [];
        const newCalls: ToolCall[] = [];
        const newActivity: ActivityEvent[] = [];

        for (const e of res.events) {
          sinceTs = Math.max(sinceTs, e.ts_ms);
          const text =
            (e.details?.["text"] as string | undefined) ??
            (e.details?.["utterance"] as string | undefined);

          if (e.action === "user.transcript" && text) {
            newTurns.push({
              id: e.id,
              ts_ms: e.ts_ms,
              speaker: "user",
              text,
            });
          } else if (e.action === "agent.utterance" && text) {
            newTurns.push({
              id: e.id,
              ts_ms: e.ts_ms,
              speaker: "agent",
              text,
            });
          } else if (e.action === "tool.call" && e.tool) {
            newCalls.push({
              id: e.id,
              ts_ms: e.ts_ms,
              tool: e.tool,
              status: e.result_status,
              duration_ms: e.duration_ms,
              details: e.details ?? {},
            });
          }

          newActivity.push(toActivityEvent(e));
        }

        if (newTurns.length) {
          setTranscript((prev) => dedupeAppend(prev, newTurns));
        }
        if (newCalls.length) {
          setToolCalls((prev) => dedupeAppend(prev, newCalls));
        }
        if (newActivity.length) {
          setActivity((prev) => {
            const merged = dedupeAppend(prev, newActivity);
            // Recompute latency stats whenever the activity stream changes —
            // for each user.transcript event, find the next agent.utterance
            // and treat the gap as response latency.
            setLatencies(computeLatencies(merged));
            return merged;
          });
        }
      } catch {
        /* transient errors are expected — keep polling */
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [sessionId]);

  // Auto-scroll transcript on new turns.
  useEffect(() => {
    transcriptScrollRef.current?.scrollTo({
      top: transcriptScrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [transcript.length]);

  // Cleanup
  useEffect(() => {
    return () => {
      cleanupAudio();
      if (roomRef.current) {
        void roomRef.current.disconnect();
        roomRef.current = null;
      }
    };
  }, []);

  function cleanupAudio() {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioCtxRef.current) {
      void audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    remoteAudioElsRef.current = [];
  }

  async function startCall() {
    setError(null);
    setStatus("connecting");
    setElapsed(0);
    setTranscript([]);
    setToolCalls([]);
    setActivity([]);
    setLatencies([]);
    setSessionId(null);
    setAudioBlocked(false);

    try {
      const { url, token, room: roomName } = await voice.startWebCall();
      setRoom(roomName);

      const lk = new Room({ adaptiveStream: true, dynacast: true });

      lk.on(RoomEvent.ParticipantConnected, (p: RemoteParticipant) => {
        if (p.identity.startsWith("agent-") || p.identity === "vooice") {
          setStatus("agent_joined");
        }
      });

      lk.on(
        RoomEvent.TrackSubscribed,
        async (
          track: RemoteTrack,
          _pub,
          participant: RemoteParticipant,
        ) => {
          if (track.kind !== Track.Kind.Audio) return;
          const el = track.attach() as HTMLAudioElement;
          el.setAttribute("data-identity", participant.identity);
          el.controls = false;
          el.muted = false;
          el.volume = 1;
          if (audioContainerRef.current) {
            audioContainerRef.current.appendChild(el);
          }
          remoteAudioElsRef.current.push(el);

          // Explicit play() — surfaces NotAllowedError if autoplay is blocked.
          try {
            await el.play();
          } catch {
            setAudioBlocked(true);
          }

          // Heuristic remote-speaking flag based on element's audio level
          // (LiveKit emits ActiveSpeakersChanged but a simple poll is enough
          // for the tiny indicator).
          setStatus("agent_joined");
        },
      );

      lk.on(RoomEvent.TrackUnsubscribed, (track: RemoteTrack) => {
        track.detach().forEach((el) => {
          el.remove();
          remoteAudioElsRef.current = remoteAudioElsRef.current.filter(
            (e) => e !== el,
          );
        });
      });

      lk.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
        const remoteActive = speakers.some(
          (s) => s.identity !== lk.localParticipant.identity,
        );
        setRemoteSpeaking(remoteActive);
      });

      lk.on(RoomEvent.Disconnected, () => setStatus("ended"));

      await lk.connect(url, token);
      await lk.localParticipant.setMicrophoneEnabled(true);
      roomRef.current = lk;
      setStatus("connected");
      setCallStarted(Date.now());

      // Wire up local mic VU meter
      attachLocalVuMeter(lk);

      // If agent participants are already in the room, mark joined.
      for (const p of lk.remoteParticipants.values()) {
        if (p.identity.startsWith("agent-") || p.identity === "vooice") {
          setStatus("agent_joined");
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setStatus("error");
    }
  }

  function attachLocalVuMeter(lk: Room) {
    const trackPub = lk.localParticipant.audioTrackPublications.values().next()
      .value;
    const localTrack = trackPub?.track as LocalAudioTrack | undefined;
    const stream = localTrack?.mediaStream;
    if (!stream) return;

    try {
      const ctx = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      src.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;

      const data = new Uint8Array(analyser.frequencyBinCount);
      const loop = () => {
        analyser.getByteTimeDomainData(data);
        // RMS over centered samples (0..1).
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i]! - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        setMicLevel(Math.min(1, rms * 3)); // boost for visibility
        rafRef.current = requestAnimationFrame(loop);
      };
      loop();
    } catch {
      /* ignore — VU meter is decorative */
    }
  }

  async function unmuteRemoteAudio() {
    setAudioBlocked(false);
    for (const el of remoteAudioElsRef.current) {
      try {
        el.muted = false;
        el.volume = 1;
        await el.play();
      } catch {
        /* still blocked */
      }
    }
  }

  async function hangUp() {
    cleanupAudio();
    if (roomRef.current) {
      await roomRef.current.disconnect();
      roomRef.current = null;
    }
    if (audioContainerRef.current) {
      audioContainerRef.current.innerHTML = "";
    }
    setStatus("ended");
  }

  function toggleMute() {
    const lk = roomRef.current;
    if (!lk) return;
    const next = !muted;
    void lk.localParticipant.setMicrophoneEnabled(!next);
    setMuted(next);
  }

  function reset() {
    setStatus("idle");
    setError(null);
    setRoom(null);
    setSessionId(null);
    setElapsed(0);
    setCallStarted(null);
    setTranscript([]);
    setToolCalls([]);
    setActivity([]);
    setLatencies([]);
    setMicLevel(0);
    setAudioBlocked(false);
    if (audioContainerRef.current) audioContainerRef.current.innerHTML = "";
  }

  const isLive = status === "connected" || status === "agent_joined";
  const canStart = status === "idle";
  const showReset = status === "ended" || status === "error";

  return (
    <div className="px-6 md:px-8 py-6 max-w-[1280px] mx-auto">
      <Mono className="block mb-2">Voice test</Mono>
      <h1 className="font-display text-3xl mb-2 leading-tight">
        Call <span className="font-display-italic">Vooice</span> from the browser.
      </h1>
      <p className="text-t2 mb-6 max-w-[58ch]">
        Direct WebRTC into the Vooice agent — no carrier, no SIP. Live transcript
        and tool-call diagnostics let you see her side of the conversation while
        you talk.
      </p>

      {/* Top bar — status + audio meters + controls */}
      <Card padding="md" glow={isLive} className={isLive ? "border-amber/30 mb-4" : "mb-4"}>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 justify-between">
          <div className="flex items-center gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                {isLive && <LiveDot />}
                <Pill tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Pill>
              </div>
              <div className="font-display text-2xl leading-none">
                {isLive ? formatElapsed(elapsed) : "00:00"}
              </div>
              {room && (
                <div className="mt-1 font-mono text-[10px] text-t4 truncate max-w-[280px]">
                  {room}
                </div>
              )}
            </div>

            {isLive && (
              <div className="flex items-center gap-6 flex-wrap">
                <div className="flex flex-col gap-2">
                  <MeterRow
                    label="You"
                    level={micLevel}
                    active={!muted && micLevel > 0.05}
                    mutedFlag={muted}
                  />
                  <MeterRow
                    label="Vooice"
                    level={remoteSpeaking ? 0.7 : 0.05}
                    active={remoteSpeaking}
                  />
                </div>
                <LatencyStats latencies={latencies} />
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {canStart && (
              <Button variant="amber" size="md" onClick={startCall}>
                Call Vooice
              </Button>
            )}
            {status === "connecting" && (
              <Button variant="ghost" size="md" disabled>
                Connecting…
              </Button>
            )}
            {isLive && (
              <>
                <Button variant="ghost" size="md" onClick={toggleMute}>
                  {muted ? "Unmute mic" : "Mute mic"}
                </Button>
                <Button
                  size="md"
                  onClick={hangUp}
                  className="!bg-rose !border-rose !text-bg hover:!brightness-110"
                >
                  Hang up
                </Button>
              </>
            )}
            {showReset && (
              <Button variant="amber" size="md" onClick={reset}>
                Try again
              </Button>
            )}
          </div>
        </div>

        {audioBlocked && (
          <div className="mt-4 border border-amber/40 bg-amber/[0.08] rounded-md px-3 py-2 text-sm flex items-center justify-between gap-3">
            <span className="text-amber">
              Browser blocked autoplay. Click to enable Vooice's voice.
            </span>
            <Button variant="amber" size="sm" onClick={unmuteRemoteAudio}>
              Unmute audio
            </Button>
          </div>
        )}

        {error && (
          <div className="mt-4 border border-rose/30 bg-rose/[0.06] rounded-md px-3 py-2 text-sm text-rose">
            {error}
          </div>
        )}

        <div ref={audioContainerRef} className="hidden" aria-hidden="true" />
      </Card>

      {/* Latency tape — per-stage breakdown of the most recent turn.
          Polls the voice.timing audit stream. Shows when session is known. */}
      {sessionId && <LatencyTape sessionId={sessionId} />}

      {/* Three-pane dashboard: transcript | activity stream | tool calls.
          Stacks on smaller viewports. Latency stats sit in the top bar. */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr_1fr] gap-4">
        <TranscriptPane
          turns={transcript}
          isLive={isLive}
          waitingForSession={isLive && !sessionId}
          scrollRef={transcriptScrollRef}
        />
        <ActivityPane
          events={activity}
          isLive={isLive}
          callStartedMs={callStarted}
        />
        <ToolCallPane calls={toolCalls} isLive={isLive} />
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

function MeterRow({
  label,
  level,
  active,
  mutedFlag,
}: {
  label: string;
  level: number;
  active: boolean;
  mutedFlag?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-t3 text-xs w-12">{label}</span>
      <div className="relative w-32 h-2 bg-bg-2 border border-line rounded-full overflow-hidden">
        <div
          className={`absolute inset-y-0 left-0 transition-[width,background-color] duration-75 ${
            mutedFlag ? "bg-t4" : active ? "bg-amber" : "bg-line"
          }`}
          style={{ width: `${Math.round(level * 100)}%` }}
        />
      </div>
      {mutedFlag && <Mono tone="amber">muted</Mono>}
    </div>
  );
}

function TranscriptPane({
  turns,
  isLive,
  waitingForSession,
  scrollRef,
}: {
  turns: TranscriptTurn[];
  isLive: boolean;
  waitingForSession: boolean;
  scrollRef: React.RefObject<HTMLDivElement | null>;
}) {
  return (
    <Card padding="none" className="overflow-hidden flex flex-col min-h-[480px] max-h-[560px]">
      <div className="px-5 py-3 border-b border-line-soft flex items-center justify-between shrink-0">
        <Mono>Transcript</Mono>
        {isLive && <Wave bars={5} height={12} />}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {turns.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center gap-2">
            {waitingForSession ? (
              <>
                <div className="font-display text-lg text-t2">
                  Waiting for Vooice to register the session…
                </div>
                <Mono>This usually takes &lt;1 second</Mono>
              </>
            ) : isLive ? (
              <>
                <div className="font-display text-lg text-t2">
                  Say hello.
                </div>
                <Mono>
                  Your speech and her replies will appear here.
                </Mono>
              </>
            ) : (
              <>
                <div className="font-display text-lg text-t3">
                  No call yet
                </div>
                <Mono>Click "Call Vooice" to begin.</Mono>
              </>
            )}
          </div>
        ) : (
          turns.map((t) => <TranscriptBubble key={t.id} turn={t} />)
        )}
      </div>
    </Card>
  );
}

function TranscriptBubble({ turn }: { turn: TranscriptTurn }) {
  const isAgent = turn.speaker === "agent";
  return (
    <div className={`flex ${isAgent ? "justify-start" : "justify-end"}`}>
      <div className="max-w-[85%] flex flex-col gap-1">
        <Mono className={isAgent ? "text-amber" : "text-t3 text-right"}>
          {isAgent ? "Vooice" : "You"}
        </Mono>
        <div
          className={[
            "rounded-2xl px-4 py-2.5 text-sm leading-relaxed",
            isAgent
              ? "bg-amber/[0.08] border border-amber/20 text-t1 italic font-display"
              : "bg-paper text-bg",
          ].join(" ")}
          style={
            isAgent
              ? { fontVariationSettings: '"opsz" 36, "SOFT" 80' }
              : undefined
          }
        >
          {turn.text}
        </div>
      </div>
    </div>
  );
}

function ToolCallPane({
  calls,
  isLive,
}: {
  calls: ToolCall[];
  isLive: boolean;
}) {
  return (
    <Card padding="none" className="overflow-hidden flex flex-col min-h-[480px] max-h-[560px]">
      <div className="px-5 py-3 border-b border-line-soft flex items-center justify-between shrink-0">
        <Mono>Tool calls</Mono>
        {calls.length > 0 && <Mono>{calls.length}</Mono>}
      </div>

      <div className="flex-1 overflow-y-auto">
        {calls.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-6 gap-2">
            <div className="font-display text-lg text-t3">
              No tool calls yet
            </div>
            <Mono>
              {isLive
                ? "When Vooice uses a tool (calendar, contacts, memory…), it shows here."
                : "Start a call to see her tool use."}
            </Mono>
          </div>
        ) : (
          calls
            .slice()
            .reverse()
            .map((c) => <ToolCallRow key={c.id} call={c} />)
        )}
      </div>
    </Card>
  );
}

function ToolCallRow({ call }: { call: ToolCall }) {
  const [expanded, setExpanded] = useState(false);
  const tone =
    call.status === "success"
      ? "ok"
      : call.status === "denied" || call.status === "failed"
      ? "rose"
      : "warn";
  return (
    <div className="border-b border-line-soft last:border-b-0">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-5 py-3 text-left hover:bg-white/[0.02] transition-colors"
      >
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="font-mono text-sm text-t1 truncate">
            {call.tool}
          </span>
          <Pill tone={tone}>{call.status}</Pill>
        </div>
        <div className="flex items-center justify-between gap-2">
          <Mono>
            {new Date(call.ts_ms).toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })}
          </Mono>
          {call.duration_ms != null && (
            <Mono>{call.duration_ms}ms</Mono>
          )}
        </div>
      </button>
      {expanded && (
        <pre className="px-5 pb-3 text-xs text-t2 overflow-x-auto font-mono">
          {JSON.stringify(call.details, null, 2)}
        </pre>
      )}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

// ── Activity stream ────────────────────────────────────────────────────────

const CATEGORY_TONE: Record<ActivityCategory, "amber" | "ok" | "rose" | "warn" | "plum" | "neutral"> = {
  user: "plum",
  agent: "amber",
  tool: "neutral",
  stage: "ok",
  policy: "warn",
  anomaly: "rose",
  system: "neutral",
};

function toActivityEvent(e: {
  id: number;
  ts_ms: number;
  action: string;
  tool: string | null;
  result_status: string;
  duration_ms: number | null;
  details: Record<string, unknown>;
}): ActivityEvent {
  // Local alias so the rest of the function reads naturally — every other
  // event surface in the app uses ``status`` for the same field.
  const status = e.result_status;
  const text = (e.details?.["text"] as string | undefined) ?? "";
  const stage = (e.details?.["stage"] as string | undefined) ?? "";
  const reason = (e.details?.["reason"] as string | undefined) ?? "";

  let category: ActivityCategory = "system";
  let summary = e.action;

  if (e.action === "user.transcript") {
    category = "user";
    summary = text ? `you · "${truncate(text, 80)}"` : "you · (silent)";
  } else if (e.action === "agent.utterance") {
    category = "agent";
    summary = text ? `vooice · "${truncate(text, 80)}"` : "vooice · (no text)";
  } else if (e.action === "tool.call") {
    category = "tool";
    const dur = e.duration_ms != null ? ` · ${e.duration_ms}ms` : "";
    summary = `${e.tool ?? "tool"} → ${status}${dur}`;
  } else if (e.action.startsWith("stage.")) {
    category = "stage";
    summary = stage ? `${e.action} → ${stage}` : e.action;
  } else if (e.action.startsWith("policy.")) {
    category = "policy";
    summary = reason ? `${e.action} · ${reason}` : `${e.action} · ${status}`;
  } else if (
    e.action === "stt.tripwire" ||
    e.action === "anomaly" ||
    status === "denied"
  ) {
    category = "anomaly";
    summary = `${e.action}${reason ? ` · ${reason}` : ""}`;
  }

  return {
    id: e.id,
    ts_ms: e.ts_ms,
    action: e.action,
    tool: e.tool,
    status,
    duration_ms: e.duration_ms,
    summary,
    category,
  };
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : s.slice(0, n - 1) + "…";
}

/** Pair each user.transcript with the next agent.utterance to derive
 *  per-turn response latency. The list is small (≤ a few dozen turns
 *  per call) so a linear scan is fine. */
function computeLatencies(events: ActivityEvent[]): LatencyStat[] {
  const sorted = [...events].sort((a, b) => a.ts_ms - b.ts_ms);
  const stats: LatencyStat[] = [];
  for (let i = 0; i < sorted.length; i++) {
    const u = sorted[i];
    if (u?.action !== "user.transcript") continue;
    for (let j = i + 1; j < sorted.length; j++) {
      const a = sorted[j];
      if (a?.action === "agent.utterance") {
        stats.push({ user_ts: u.ts_ms, agent_ts: a.ts_ms });
        break;
      }
      if (a?.action === "user.transcript") break; // user spoke twice in a row
    }
  }
  return stats;
}

function LatencyStats({ latencies }: { latencies: LatencyStat[] }) {
  if (latencies.length === 0) {
    return (
      <div className="flex flex-col gap-1">
        <Mono>response · —</Mono>
        <Mono>turns · 0</Mono>
      </div>
    );
  }
  const last = latencies[latencies.length - 1]!;
  const lastMs = last.agent_ts - last.user_ts;
  const avgMs =
    latencies.reduce((s, l) => s + (l.agent_ts - l.user_ts), 0) /
    latencies.length;
  const fmt = (ms: number) =>
    ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;

  const tone =
    lastMs > 3000 ? "text-rose" : lastMs > 1500 ? "text-amber" : "text-amber";
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-t3">last</span>
        <span className={`font-mono text-[12px] tabular-nums ${tone}`}>
          {fmt(lastMs)}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-t3">avg</span>
        <span className="font-mono text-[12px] tabular-nums text-t2">
          {fmt(avgMs)}
        </span>
        <span className="font-mono text-[10px] text-t4">· {latencies.length} turns</span>
      </div>
    </div>
  );
}

function ActivityPane({
  events,
  isLive,
  callStartedMs,
}: {
  events: ActivityEvent[];
  isLive: boolean;
  callStartedMs: number | null;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [events.length]);

  return (
    <Card padding="none" className="overflow-hidden flex flex-col min-h-[480px] max-h-[560px]">
      <div className="px-5 py-3 border-b border-line-soft flex items-center justify-between shrink-0">
        <Mono>Pipeline activity</Mono>
        {events.length > 0 && <Mono>{events.length}</Mono>}
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        {events.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-6 gap-2">
            <div className="font-display text-lg text-t3">
              No events yet
            </div>
            <Mono>
              {isLive
                ? "STT → LLM → TTS events will appear here as they fire."
                : "Start a call to see the pipeline."}
            </Mono>
          </div>
        ) : (
          events.map((e) => (
            <ActivityRow
              key={e.id}
              event={e}
              callStartedMs={callStartedMs}
            />
          ))
        )}
      </div>
    </Card>
  );
}

function ActivityRow({
  event,
  callStartedMs,
}: {
  event: ActivityEvent;
  callStartedMs: number | null;
}) {
  const tone = CATEGORY_TONE[event.category];
  const colorClass = {
    amber: "text-amber",
    ok: "text-emerald-300",
    rose: "text-rose",
    warn: "text-amber-deep",
    plum: "text-[#C4A6D6]",
    neutral: "text-t2",
  }[tone];
  const elapsed =
    callStartedMs != null
      ? `+${((event.ts_ms - callStartedMs) / 1000).toFixed(1)}s`
      : new Date(event.ts_ms).toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        });

  return (
    <div className="px-4 py-2 border-b border-line-soft last:border-b-0 flex items-baseline gap-3 hover:bg-white/[0.02]">
      <span className="font-mono text-[10px] tabular-nums text-t4 shrink-0 w-14">
        {elapsed}
      </span>
      <span
        className={`font-mono text-[10px] uppercase tracking-[0.14em] shrink-0 w-10 ${colorClass}`}
      >
        {event.category.slice(0, 4)}
      </span>
      <span className="text-[12px] text-t1 truncate flex-1 leading-snug">
        {event.summary}
      </span>
    </div>
  );
}

// ── LatencyTape ────────────────────────────────────────────────────────────
//
// Polls GET /api/voice/sessions/:id/timing (Agent A's voice.timing events)
// and renders a horizontal timeline of per-stage colored bars for the most
// recent turn (user.final_transcript → tts.first_audio), plus a summary
// header line.
//
// Color mapping (aligned with design tokens — no new token needed):
//   VAD / STT  → sky-400 / sky-300 (Tailwind built-in)
//   LLM        → amber / amber (existing token)
//   Tool       → plum / [#C4A6D6] (existing token)
//   TTS        → emerald-400 / emerald-300 (existing token via "ok")
//   marker     → t4 (neutral tick)

/** Stages that belong to each pipeline bucket */
const VAD_STT_STAGES = new Set([
  "user.speech_started",
  "user.partial_transcript",
  "user.final_transcript",
]);
const LLM_STAGES = new Set([
  "llm.request",
  "llm.first_token",
  "llm.last_token",
]);
const TOOL_STAGES = new Set(["tool.call", "tool.result"]);
const TTS_STAGES = new Set(["tts.first_audio", "tts.last_audio"]);

function stageColor(stage: string): { bar: string; bg: string } {
  if (VAD_STT_STAGES.has(stage))
    return { bar: "bg-sky-400", bg: "bg-sky-400/15" };
  if (LLM_STAGES.has(stage))
    return { bar: "bg-amber", bg: "bg-amber/15" };
  if (TOOL_STAGES.has(stage))
    return { bar: "bg-[#C4A6D6]", bg: "bg-[#C4A6D6]/15" };
  if (TTS_STAGES.has(stage))
    return { bar: "bg-emerald-400", bg: "bg-emerald-400/15" };
  return { bar: "bg-t3", bg: "bg-t3/10" };
}

/**
 * Extract the most recent "turn" from the events list.
 * A turn is the slice from the last `user.final_transcript` event through
 * the next `tts.first_audio` (inclusive).
 */
function extractLastTurn(events: VoiceTimingEvent[]): VoiceTimingEvent[] {
  let finalIdx = -1;
  for (let i = events.length - 1; i >= 0; i--) {
    if (events[i]?.stage === "user.final_transcript") {
      finalIdx = i;
      break;
    }
  }
  if (finalIdx === -1) return [];

  // Find the tts.first_audio that follows
  let ttsIdx = events.length - 1; // default: include everything to the end
  for (let i = finalIdx + 1; i < events.length; i++) {
    if (events[i]?.stage === "tts.first_audio") {
      ttsIdx = i;
      break;
    }
  }
  return events.slice(finalIdx, ttsIdx + 1);
}

/** Header summary values for the last turn */
interface TurnSummary {
  vadMs: number | null;   // user.final_transcript metadata.endpoint_ms
  sttMs: number | null;   // partial→final gap (not directly available — show —)
  llmMs: number | null;   // llm.first_token.latency_ms
  toolMs: number | null;  // tool.result.latency_ms (last tool in turn)
  ttsMs: number | null;   // tts.first_audio.latency_ms
  totalMs: number | null; // user.final_transcript → tts.first_audio wall time
}

function computeTurnSummary(turnEvents: VoiceTimingEvent[]): TurnSummary {
  const finalEvt = turnEvents.find((e) => e.stage === "user.final_transcript");
  const ttsEvt = turnEvents.find((e) => e.stage === "tts.first_audio");
  const llmEvt = turnEvents.find((e) => e.stage === "llm.first_token");
  // Last tool.result in the turn (may be absent)
  const toolEvt = [...turnEvents].reverse().find((e) => e.stage === "tool.result");

  const vadMs =
    finalEvt?.metadata != null &&
    typeof (finalEvt.metadata as Record<string, unknown>)["endpoint_ms"] === "number"
      ? ((finalEvt.metadata as Record<string, unknown>)["endpoint_ms"] as number)
      : null;

  const llmMs = llmEvt?.latency_ms ?? null;
  const toolMs = toolEvt?.latency_ms ?? null;
  const ttsMs = ttsEvt?.latency_ms ?? null;

  const totalMs =
    finalEvt && ttsEvt
      ? ttsEvt.timestamp_ms - finalEvt.timestamp_ms
      : null;

  return { vadMs, sttMs: null, llmMs, toolMs, ttsMs, totalMs };
}

function fmtMs(ms: number | null): string {
  if (ms == null) return "—";
  return `${ms}ms`;
}

function LatencyTape({ sessionId }: { sessionId: string }) {
  const { data } = useQuery({
    queryKey: ["voice-timing", sessionId],
    queryFn: () => voice.getTimings(sessionId),
    refetchInterval: 2000,
    staleTime: 0,
  });

  const events = data?.events ?? [];
  const turnEvents = extractLastTurn(events);

  if (events.length === 0) {
    return (
      <Card padding="sm" className="mb-4">
        <div className="flex items-center gap-3">
          <Mono>Latency tape</Mono>
          <span className="text-xs text-t3 font-mono">
            Place a call to see timing data.
          </span>
        </div>
      </Card>
    );
  }

  if (turnEvents.length === 0) {
    return (
      <Card padding="sm" className="mb-4">
        <div className="flex items-center gap-3">
          <Mono>Latency tape</Mono>
          <span className="text-xs text-t3 font-mono">
            Waiting for first user turn…
          </span>
        </div>
      </Card>
    );
  }

  const summary = computeTurnSummary(turnEvents);

  // Timeline geometry — map timestamp_ms → pixel position within a 100% bar
  const minTs = Math.min(...turnEvents.map((e) => e.timestamp_ms));
  const maxTs = Math.max(...turnEvents.map((e) => e.timestamp_ms));
  const span = Math.max(maxTs - minTs, 1); // avoid division by zero

  const pct = (ts: number) => ((ts - minTs) / span) * 100;

  return (
    <Card padding="sm" className="mb-4">
      {/* Header summary */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3">
        <Mono>Latency tape</Mono>
        <span className="font-mono text-[11px] text-t2 tracking-tight">
          Last turn:
          <span className="text-sky-300"> VAD {fmtMs(summary.vadMs)}</span>
          <span className="text-t4"> · </span>
          <span className="text-sky-300">STT {fmtMs(summary.sttMs)}</span>
          <span className="text-t4"> · </span>
          <span className="text-amber">LLM {fmtMs(summary.llmMs)}</span>
          <span className="text-t4"> · </span>
          <span className="text-[#C4A6D6]">Tool {fmtMs(summary.toolMs)}</span>
          <span className="text-t4"> · </span>
          <span className="text-emerald-300">TTS {fmtMs(summary.ttsMs)}</span>
          {summary.totalMs != null && (
            <>
              <span className="text-t4"> = </span>
              <span className="text-t1 font-semibold">{summary.totalMs}ms</span>
            </>
          )}
        </span>
      </div>

      {/* Horizontal timeline strip */}
      <div
        className="relative w-full h-6 bg-bg-2 border border-line rounded-md overflow-hidden"
        title="Voice pipeline timeline — most recent turn"
        aria-label="Voice pipeline timeline"
      >
        {turnEvents.map((evt, i) => {
          const isMarker = evt.stage === "stage.transition";
          const colors = stageColor(evt.stage);
          const left = pct(evt.timestamp_ms);

          if (isMarker) {
            // Stage transitions: thin vertical tick
            return (
              <div
                key={i}
                className="absolute inset-y-0 w-px bg-t4 opacity-70"
                style={{ left: `${left}%` }}
                title={`stage.transition @ ${evt.timestamp_ms}`}
              />
            );
          }

          // Duration bars: use latency_ms if available to set width,
          // otherwise render as a narrow 2px marker so it's always visible.
          const durationPct =
            evt.latency_ms != null && span > 0
              ? (evt.latency_ms / span) * 100
              : 0;
          const widthPct = Math.max(durationPct, 0.8); // min 0.8% for visibility

          return (
            <div
              key={i}
              className={`absolute inset-y-0 ${colors.bar} opacity-80`}
              style={{
                left: `${left}%`,
                width: `${widthPct}%`,
              }}
              title={`${evt.stage}${evt.latency_ms != null ? ` · ${evt.latency_ms}ms` : ""}`}
            />
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2">
        {[
          { label: "VAD/STT", cls: "bg-sky-400" },
          { label: "LLM", cls: "bg-amber" },
          { label: "Tool", cls: "bg-[#C4A6D6]" },
          { label: "TTS", cls: "bg-emerald-400" },
        ].map(({ label, cls }) => (
          <div key={label} className="flex items-center gap-1.5">
            <span className={`inline-block w-2.5 h-2.5 rounded-sm ${cls} opacity-80`} />
            <span className="font-mono text-[10px] tracking-[0.14em] uppercase text-t3">
              {label}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function dedupeAppend<T extends { id: number }>(
  prev: T[],
  next: T[],
): T[] {
  const seen = new Set(prev.map((p) => p.id));
  const fresh = next.filter((n) => !seen.has(n.id));
  if (fresh.length === 0) return prev;
  return [...prev, ...fresh];
}

function formatElapsed(s: number): string {
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${m.toString().padStart(2, "0")}:${ss.toString().padStart(2, "0")}`;
}
